import { useEffect, useState } from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/ModalComponents';
import { useUser } from '@store';
import { useInsertMobilityVehicleMutation } from '../queries/insertMobilityVehicle.mutation.generated';
import { useUpdateMobilityVehicleMutation } from '../queries/updateMobilityVehicle.mutation.generated';
import { Mobility_Vehicles } from '@gql/base-types';

interface VehicleFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  vehicle?: Mobility_Vehicles | null;
  customerId: number;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  make: string;
  model: string;
  year: string;
  color: string;
  license_plate: string;
  vin: string;
  capacity: string;
  status: string;
}

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'in_use', label: 'In Use' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' },
];

const blankForm: FormState = {
  make: '',
  model: '',
  year: '',
  color: '',
  license_plate: '',
  vin: '',
  capacity: '',
  status: 'available',
};

const toInt = (v: string): number | null => {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : null;
};

const VehicleFormDialog: React.FC<VehicleFormDialogProps> = ({
  open,
  mode,
  vehicle,
  customerId,
  onClose,
  onSaved,
}) => {
  const user = useUser();
  const userEmail = user?.profile?.email || 'unknown';

  const [form, setForm] = useState<FormState>(blankForm);
  // What the form opened with, so an untouched edit holds nothing.
  const [seed, setSeed] = useState<FormState>(blankForm);

  const [insertVehicle, { loading: inserting }] = useInsertMobilityVehicleMutation();
  const [updateVehicle, { loading: updating }] = useUpdateMobilityVehicleMutation();
  const saving = inserting || updating;

  // Seed the form whenever the dialog opens (edit → from vehicle, create → blank).
  useEffect(() => {
    if (!open) return;
    const opened: FormState =
      mode === 'edit' && vehicle
        ? {
            make: vehicle.make ?? '',
            model: vehicle.model ?? '',
            year: vehicle.year != null ? `${vehicle.year}` : '',
            color: vehicle.color ?? '',
            license_plate: vehicle.license_plate ?? '',
            vin: vehicle.vin ?? '',
            capacity: vehicle.capacity != null ? `${vehicle.capacity}` : '',
            status: vehicle.status ?? 'available',
          }
        : blankForm;
    setForm(opened);
    setSeed(opened);
  }, [open, mode, vehicle]);

  // Hold an app update while the open form differs from what it opened with, or a save is running.
  const formChanged = (Object.keys(form) as (keyof FormState)[]).some(key => form[key] !== seed[key]);
  useReloadGuard((open && formChanged) || saving, 'Vehicle');

  const setField = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  const canSave = !!form.make.trim() && !!form.model.trim() && !saving;

  const handleError = (err: any) => {
    const message = String(err?.message ?? '');
    if (message.includes('mobility_vehicles_vin_customer_idx') || message.toLowerCase().includes('unique')) {
      toast.error('A vehicle with that VIN already exists for this dealership.');
    } else {
      toast.error(`Failed to save vehicle: ${err?.message ?? 'Unknown error'}`);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;

    const fields = {
      make: form.make.trim(),
      model: form.model.trim(),
      year: toInt(form.year),
      color: form.color.trim() || null,
      license_plate: form.license_plate.trim() || null,
      vin: form.vin.trim() || null,
      capacity: toInt(form.capacity),
      status: form.status,
    };

    try {
      if (mode === 'edit' && vehicle) {
        await updateVehicle({
          variables: { id: vehicle.id, set: { ...fields, updated_by: userEmail } },
        });
        toast.success('Vehicle updated.');
      } else {
        await insertVehicle({
          variables: {
            object: { ...fields, customer_id: customerId, created_by: userEmail, updated_by: userEmail },
          },
        });
        toast.success('Vehicle added.');
      }
      onSaved();
      onClose();
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <Modal open={open} width='sm' handleClose={onClose}>
      <ModalHeader handleClose={onClose}>{mode === 'edit' ? 'Edit Vehicle' : 'Add Vehicle'}</ModalHeader>

      <ModalContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              size='small'
              label='Make'
              value={form.make}
              onChange={setField('make')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              size='small'
              label='Model'
              value={form.model}
              onChange={setField('model')}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='Year'
              value={form.year}
              onChange={setField('year')}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField fullWidth size='small' label='Color' value={form.color} onChange={setField('color')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='Capacity'
              value={form.capacity}
              onChange={setField('capacity')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size='small'
              label='License Plate'
              value={form.license_plate}
              onChange={setField('license_plate')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size='small' label='VIN' value={form.vin} onChange={setField('vin')} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              select
              size='small'
              label='Status'
              value={form.status}
              onChange={setField('status')}
            >
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </ModalContent>

      <ModalFooter>
        <ModalAction color='inherit' onClick={onClose} disabled={saving}>
          Cancel
        </ModalAction>
        <ModalAction variant='contained' onClick={handleSave} disabled={!canSave} loading={saving}>
          {mode === 'edit' ? 'Save Changes' : 'Add Vehicle'}
        </ModalAction>
      </ModalFooter>
    </Modal>
  );
};

export default VehicleFormDialog;
